import { type ChangeEvent, type KeyboardEvent, type MouseEvent, memo, type Ref } from 'react';

import { ChevronDownIcon, XIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentGuidedVariantSizeShape } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSizeShape';
import {
  type WorkspaceAgentScaleOrCropSize,
  workspaceAgentScaleOrCropSizeKey,
  workspaceAgentScaleOrCropSizeLabel,
  workspaceAgentScaleOrCropSizeShape,
  workspaceAgentScaleOrCropSizeText,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentScaleOrCrop';

export type WorkspaceAgentGuidedVariantSizeComboboxProps = {
  inputRef: Ref<HTMLInputElement>;
  isOpen: boolean;
  listId: string;
  onInputKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onMenuToggle: () => void;
  onOpen: () => void;
  onQueryChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onRemovePill: (event: MouseEvent<HTMLButtonElement>) => void;
  placeholder: string;
  query: string;
  sizes: readonly WorkspaceAgentScaleOrCropSize[];
};

export const WorkspaceAgentGuidedVariantSizeCombobox = memo(
  ({
    inputRef,
    isOpen,
    listId,
    onInputKeyDown,
    onMenuToggle,
    onOpen,
    onQueryChange,
    onRemovePill,
    placeholder,
    query,
    sizes,
  }: WorkspaceAgentGuidedVariantSizeComboboxProps) => (
    <div className="flex min-h-8 w-full items-center gap-1 rounded-8 border border-gray-5 bg-gray-1 py-1 pr-1 pl-1.5">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
        {sizes.map((size) => {
          const label = workspaceAgentScaleOrCropSizeLabel(size);
          const pixels = workspaceAgentScaleOrCropSizeText(size);
          const shape = workspaceAgentScaleOrCropSizeShape(size);

          return (
            <span
              className="inline-flex h-6 max-w-full items-center gap-1 rounded-4 bg-gray-4 pr-0.5 pl-2 font-medium text-12 text-gray-12 tabular-nums"
              data-size-key={workspaceAgentScaleOrCropSizeKey(size)}
              data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_PILL"
              key={workspaceAgentScaleOrCropSizeKey(size)}
              title={label === pixels ? undefined : pixels}
            >
              {shape ? <WorkspaceAgentGuidedVariantSizeShape height={shape.height} width={shape.width} /> : null}
              <span className="truncate">{label}</span>
              <button
                aria-label={`Remove ${label}`}
                className="flex size-5 shrink-0 items-center justify-center rounded-4 text-gray-9 hover:bg-gray-5 hover:text-gray-12"
                data-size-key={workspaceAgentScaleOrCropSizeKey(size)}
                data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_PILL_REMOVE"
                onClick={onRemovePill}
                type="button"
              >
                <XIcon aria-hidden className="size-3" />
              </button>
            </span>
          );
        })}
        <input
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={isOpen}
          aria-label="Image sizes"
          autoComplete="off"
          className="h-6 min-w-24 flex-1 bg-transparent px-1 text-14 text-gray-12 outline-none placeholder:text-gray-9"
          data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_INPUT"
          onChange={onQueryChange}
          onClick={onOpen}
          onFocus={onOpen}
          onKeyDown={onInputKeyDown}
          placeholder={placeholder}
          ref={inputRef}
          role="combobox"
          spellCheck={false}
          value={query}
        />
      </div>
      <button
        aria-label="Size presets"
        className="flex size-6 shrink-0 items-center justify-center self-start text-gray-9 hover:text-gray-12"
        data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_TOGGLE"
        onClick={onMenuToggle}
        tabIndex={-1}
        type="button"
      >
        <ChevronDownIcon aria-hidden className={cn('size-4', isOpen && 'rotate-180')} />
      </button>
    </div>
  ),
);

WorkspaceAgentGuidedVariantSizeCombobox.displayName = 'WorkspaceAgentGuidedVariantSizeCombobox';
